import { useAtom } from 'jotai';
import { orderBy } from 'lodash';
import { useEffect } from 'react';

import {
  useFetchDevicesByOrg,
  useFetchIsSyncInProgress
} from '@/features/DeviceCreate/DeviceCreate.hooks';
import {
  devicesAtom,
  groupedDevicesAtom,
  updateDeviceDTO
} from '@/pages/Administration/Devices';
import type {
  DeviceListingResponseDTO,
  DeviceSyncDataDTO
} from '@/pages/Administration/Devices/SyncDevices/SyncDevices.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import type { DeviceModel } from './DevicesTable.types';

/**
 * Hook to fetch and process device listing data
 * Handles Lima devices, other devices, and deleted records
 */
export const useDeviceListing = () => {
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [, setDevices] = useAtom(devicesAtom);
  const [, setGroupedDevices] = useAtom(groupedDevicesAtom);

  const devicesQuery = useFetchDevicesByOrg(selectedOrgData);
  const isSyncInProgressQuery = useFetchIsSyncInProgress();

  useEffect(() => {
    const { isSuccess, data } = devicesQuery;

    // Lima Devices, Other devices and Deleted records are also fetching in the devices API response,
    // showing the decommissioning status while the sync is in progress.
    const devicesResponse = data as unknown as DeviceListingResponseDTO;

    if (isSuccess && data && devicesResponse?.other_devices) {
      const limaSourceAggregatedStatus =
        devicesResponse?.lima_source_aggregated_status || [];
      const devicesDeployedData =
        (devicesResponse?.other_devices as DeviceSyncDataDTO[]) || [];
      const devicesData: DeviceModel[] = [
        // Process deployed devices with comparison to current state
        ...devicesDeployedData
          .filter((deviceRow) => deviceRow.devices !== null)
          .map(({ devices, devices_deployed }) => {
            // actionType is used to determine if the device is new, updated or deleted
            if (devices_deployed !== null) {
              if (
                devices?.configuration !== devices_deployed?.configuration ||
                devices?.device_size?.id !== devices_deployed?.device_size_id
              ) {
                return {
                  ...devices,
                  org_label: devices?.organization?.label || '',
                  action: 'Changed',
                  actionType: 'Updated',
                  isSyncInProgress: devices?.devices_tf_jobs !== null
                } as DeviceModel;
              }
            }
            return {
              ...devices,
              org_label: devices?.organization?.label || '',
              action: 'UnChanged',
              actionType: '',
              isSyncInProgress: devices?.devices_tf_jobs !== null
            } as DeviceModel;
          }),
        // Process deleted devices (devices is null, but devices_deployed exists)
        ...devicesDeployedData
          .filter(
            (deviceRow) =>
              deviceRow.devices === null && deviceRow?.devices_deployed !== null
          )
          .map(({ devices, devices_deployed }) => {
            const device = {
              ...devices,
              id: 0,
              created_at: '',
              updated_at: '',
              device_uid: devices_deployed?.device_uid || '',
              host_name: devices_deployed?.host_name || '',
              display_name: devices_deployed?.host_name || '',
              device_type_id: devices_deployed?.device_type_id || 0,
              ip_address: null,
              organization_id: devices_deployed?.organization_id || 0,
              device_type: devices_deployed?.device_type || '',
              sources: [],
              description: '',
              organization: devices_deployed?.organization || '',
              device_statuses: devices_deployed?.device_statuses || [],
              last_seen: '',
              org_label: devices_deployed?.organization?.label || '',
              action: 'Changed',
              actionType: 'Deleted',
              isSyncInProgress: devices_deployed?.devices_tf_jobs !== null,
              lima_source_aggregated_status: []
            };
            return device as unknown as DeviceModel;
          })
      ];

      // For Lima devices, add Lima Sources' aggregated status data to devicesData
      // and display only devices that have aggregated status data.
      const limaDevices =
        (devicesResponse?.lima_devices as DeviceModel[]) || [];
      limaDevices.forEach((limaDeviceRow) => {
        let limaDevice = limaDeviceRow;
        const aggregatedStatusData = limaSourceAggregatedStatus.filter(
          (sourceType: { device_uid: string }) =>
            sourceType.device_uid === limaDevice.device_uid
        );
        // Add to devicesData only if aggregatedStatusData is not empty
        if (aggregatedStatusData && aggregatedStatusData.length > 0) {
          limaDevice = {
            ...limaDevice,
            org_label: '',
            lima_source_aggregated_status: [...aggregatedStatusData],
            action: 'UnChanged',
            actionType: '',
            isSyncInProgress: false
          } as DeviceModel;
          devicesData.push(limaDevice);
        }
      });

      let deviceModelData = updateDeviceDTO(devicesData as DeviceModel[]);
      deviceModelData = orderBy(deviceModelData, 'host_name', 'asc');
      setDevices(deviceModelData);
    } else {
      setDevices([]);
      setGroupedDevices([]);
    }
  }, [
    devicesQuery.isSuccess,
    devicesQuery.data,
    setDevices,
    setGroupedDevices
  ]);

  return {
    devicesQuery,
    isSyncInProgressQuery
  };
};
